/**
 * O manifesto de capacidades e a escolha de contexto — T18 e T4c da spec #408.
 *
 * Quem manda no contexto é a escolha gravada no servidor, não um cabeçalho montado pela tela:
 * por isso trocar de contexto é um POST seguido de um GET, e não um `X-Company-Id` novo.
 */

import { isApiError } from '../types/common';
import type { ContextoAtivo, ContextoDisponivel, Manifesto, Perspectiva } from '../types/manifesto';
import { serializarContexto } from '../utils/manifestoMenu';

import apiClient from './apiClient';

/** Falta escolher entre os contextos que a resposta traz. */
const ESCOLHA_PENDENTE = 'CONTEXTO_NAO_ESCOLHIDO';

/** Não há contexto nenhum para escolher: a conta existe e não foi vinculada. */
const SEM_VINCULO = 'SEM_CONTEXTO';

const PERSPECTIVAS: readonly Perspectiva[] = ['empresa', 'prestador', 'plataforma'];

function listaDeContextos(valor: unknown): ContextoDisponivel[] {
  if (!Array.isArray(valor)) return [];
  return valor.filter((item): item is ContextoDisponivel => {
    if (typeof item !== 'object' || item === null) return false;
    return PERSPECTIVAS.includes((item as ContextoDisponivel).tipo);
  });
}

/**
 * Os contextos que uma recusa de contexto oferece, ou `null` se a falha for outra coisa.
 *
 * O servidor nunca manda manifesto com `precisaEscolher` de pé: quando falta escolher, ele
 * recusa com 403 e a lista de opções; sem vínculo nenhum, recusa com 403 e sem lista. Lista
 * vazia é resposta legítima — é o que leva ao vazio que diz a quem pedir acesso.
 */
export function contextosDaRecusa(erro: unknown): ContextoDisponivel[] | null {
  if (!isApiError(erro)) return null;
  if (erro.code === SEM_VINCULO) return [];
  if (erro.code !== ESCOLHA_PENDENTE) return null;
  return listaDeContextos(erro.details?.['contextos']);
}

class ManifestoService {
  /**
   * O cliente HTTP já entrega o corpo da resposta, e o manifesto vem cru, sem envelope.
   * Desembrulhar mais uma camada aqui devolvia vazio, e a tela ficava presa em "Carregando
   * seu acesso..." — nenhum teste pegou porque todos simulavam este método inteiro.
   */
  async obter(): Promise<Manifesto> {
    return (await apiClient.get('/manifesto')) as unknown as Manifesto;
  }

  async definirContexto(contexto: ContextoAtivo): Promise<void> {
    await apiClient.post('/contexto-ativo', { contexto: serializarContexto(contexto) });
  }
}

export const manifestoService = new ManifestoService();
export default manifestoService;
